<template>
  <NavBar />
  <v-container fluid style="margin-top: 185px;">
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-4">
          <h1 class="text-h4">Alerte Trafic & Profile Limitari</h1>
          <div>
            <v-btn color="success" variant="flat" class="mr-2" @click="runProcessAlerts" :loading="loadingProcess">
              <v-icon start>mdi-play</v-icon>
              Ruleaza Alerte
            </v-btn>
            <v-btn color="info" variant="outlined" class="mr-2" @click="runDryRun" :loading="loadingDryRun">
              <v-icon start>mdi-play-circle-outline</v-icon>
              Test Dry Run
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-tabs v-model="activeTab" color="primary" class="mb-4">
      <v-tab value="rules">Reguli Alerte</v-tab>
      <v-tab value="profiles">Profile Limitari</v-tab>
      <v-tab value="logs">Jurnal Alerte</v-tab>
    </v-tabs>

    <v-window v-model="activeTab">
      <!-- TAB 1: RULES -->
      <v-window-item value="rules">
        <v-row>
          <v-col cols="12">
            <v-card variant="outlined" class="mb-2">
              <v-card-text class="py-2">
                <v-row dense align="center">
                  <v-col cols="12" md="3">
                    <v-select v-model="ruleFilters.metric_type" label="Metrica" :items="[{title:'Toate',value:''},...metricTypes]" item-title="title" item-value="value" density="compact" hide-details clearable />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-select v-model="ruleFilters.action_type" label="Actiune" :items="[{title:'Toate',value:''},...actionTypes]" item-title="title" item-value="value" density="compact" hide-details clearable />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-select v-model="ruleFilters.enabled" label="Status" :items="[{title:'Toate',value:''},{title:'Active',value:'true'},{title:'Inactive',value:'false'}]" item-title="title" item-value="value" density="compact" hide-details />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-switch v-model="ruleFilters.standaloneOnly" label="Doar standalone" color="primary" density="compact" hide-details />
                  </v-col>
                  <v-col cols="12" md="2" class="d-flex gap-1">
                    <v-btn icon="mdi-magnify" color="primary" variant="tonal" size="small" @click="loadRules" :loading="loadingRules" />
                    <v-btn color="primary" size="small" @click="openRuleDialog(null)">
                      <v-icon start>mdi-plus</v-icon> Regula
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-data-table
              :headers="ruleHeaders"
              :items="rules"
              :loading="loadingRules"
              density="compact"
              class="elevation-1"
              items-per-page="25"
            >
              <template #[`item.enabled`]="{ item }">
                <v-switch :model-value="item.enabled" color="success" density="compact" hide-details @update:model-value="toggleRule(item)" />
              </template>
              <template #[`item.metric_type`]="{ item }">
                <v-chip size="small" :color="metricColor(item.metric_type)">{{ item.metric_type_label }}</v-chip>
              </template>
              <template #[`item.threshold_value`]="{ item }">
                {{ item.threshold_value }} {{ metricUnitFor(item.metric_type) }}
              </template>
              <template #[`item.action_type`]="{ item }">
                <v-chip size="small" :color="actionColor(item.action_type)">{{ item.action_type_label }}</v-chip>
              </template>
              <template #[`item.network_type`]="{ item }">
                <v-chip size="x-small" variant="outlined">{{ item.network_type_label }}</v-chip>
              </template>
              <template #[`item.period_type`]="{ item }">
                <v-chip size="x-small" variant="outlined">{{ item.period_type_label }}</v-chip>
              </template>
              <template #[`item.target`]="{ item }">
                <v-chip v-if="item.sim_msisdn" size="x-small" color="orange">SIM {{ item.sim_msisdn }}</v-chip>
                <v-chip v-else-if="item.sim_id" size="x-small" color="orange">SIM #{{ item.sim_id }}</v-chip>
                <v-chip v-else-if="item.imsi_group_name" size="x-small" color="blue">{{ item.imsi_group_name }}</v-chip>
                <span v-else class="text-grey-lighten-1">-</span>
              </template>
              <template #[`item.profile_name`]="{ item }">
                <span v-if="item.profile_name">{{ item.profile_name }}</span>
                <span v-else class="text-grey-lighten-1">Standalone</span>
              </template>
              <template #[`item.actions`]="{ item }">
                <v-btn icon="mdi-pencil" variant="text" size="small" @click="openRuleDialog(item)" />
                <v-btn icon="mdi-delete" variant="text" size="small" color="error" @click="confirmDeleteRule(item)" />
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-window-item>

      <!-- TAB 2: PROFILES -->
      <v-window-item value="profiles">
        <v-row>
          <v-col cols="12" class="d-flex justify-end mb-2">
            <v-btn color="primary" @click="openProfileDialog(null)">
              <v-icon start>mdi-plus</v-icon> Profil Nou
            </v-btn>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="4" v-for="profile in profiles" :key="profile.id">
            <v-card :variant="selectedProfileId === profile.id ? 'elevated' : 'outlined'" :color="selectedProfileId === profile.id ? 'primary' : undefined" @click="selectProfile(profile)" class="cursor-pointer">
              <v-card-title class="d-flex align-center">
                <v-icon :color="profile.enabled ? 'success' : 'grey'" class="mr-2">{{ profile.enabled ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
                {{ profile.name }}
                <v-spacer />
                <v-btn icon="mdi-pencil" variant="text" size="x-small" @click.stop="openProfileDialog(profile)" />
                <v-btn icon="mdi-delete" variant="text" size="x-small" color="error" @click.stop="confirmDeleteProfile(profile)" />
              </v-card-title>
              <v-card-text>
                <div v-if="profile.description" class="text-caption mb-1">{{ profile.description }}</div>
                <v-chip size="small" class="mr-1">{{ profile.rules_count || 0 }} reguli</v-chip>
                <v-chip size="small">{{ profile.assignments_count || 0 }} asignari</v-chip>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Profile Detail Panel -->
        <v-row v-if="selectedProfileId" class="mt-4">
          <v-col cols="12" md="7">
            <v-card variant="outlined">
              <v-card-title>
                Reguli din Profil: {{ selectedProfile?.name }}
                <v-spacer />
                <v-btn color="primary" size="small" @click="openRuleDialogForProfile">
                  <v-icon start>mdi-plus</v-icon> Regula
                </v-btn>
              </v-card-title>
              <v-card-text>
                <v-data-table
                  :headers="profileRuleHeaders"
                  :items="profileRules"
                  :loading="loadingProfileRules"
                  density="compact"
                  items-per-page="10"
                >
                  <template #[`item.metric_type`]="{ item }">
                    <v-chip size="small" :color="metricColor(item.metric_type)">{{ item.metric_type_label }}</v-chip>
                  </template>
                  <template #[`item.threshold_value`]="{ item }">
                    {{ item.threshold_value }} {{ metricUnitFor(item.metric_type) }}
                  </template>
                  <template #[`item.action_type`]="{ item }">
                    <v-chip size="small" :color="actionColor(item.action_type)">{{ item.action_type_label }}</v-chip>
                  </template>
                  <template #[`item.enabled`]="{ item }">
                    <v-switch :model-value="item.enabled" color="success" density="compact" hide-details @update:model-value="toggleRule(item)" />
                  </template>
                  <template #[`item.actions`]="{ item }">
                    <v-btn icon="mdi-pencil" variant="text" size="small" @click="openRuleDialog(item)" />
                    <v-btn icon="mdi-delete" variant="text" size="small" color="error" @click="confirmDeleteRule(item)" />
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="5">
            <v-card variant="outlined">
              <v-card-title>
                Asignari
                <v-spacer />
                <v-btn color="primary" size="small" @click="showAssignmentDialog = true">
                  <v-icon start>mdi-plus</v-icon> Asigneaza
                </v-btn>
              </v-card-title>
              <v-card-text>
                <v-list density="compact">
                  <v-list-item v-for="a in assignments" :key="a.id">
                    <template #prepend>
                      <v-icon :color="a.imsi_group_id ? 'blue' : 'orange'">{{ a.imsi_group_id ? 'mdi-group' : 'mdi-sim' }}</v-icon>
                    </template>
                    <v-list-item-title>
                      <span v-if="a.imsi_group_name">Grup: {{ a.imsi_group_name }}</span>
                      <span v-else>SIM #{{ a.sim_id }} <span v-if="a.sim_msisdn">({{ a.sim_msisdn }})</span></span>
                    </v-list-item-title>
                    <template #append>
                      <v-btn icon="mdi-delete" variant="text" size="small" color="error" @click="confirmDeleteAssignment(a)" />
                    </template>
                  </v-list-item>
                  <v-list-item v-if="!assignments.length">
                    <v-list-item-title class="text-grey">Nicio asignare</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-window-item>

      <!-- TAB 3: LOGS -->
      <v-window-item value="logs">
        <v-row>
          <v-col cols="12">
            <v-card variant="outlined" class="mb-2">
              <v-card-text class="py-2">
                <v-row dense align="center">
                  <v-col cols="12" md="3">
                    <v-select v-model="logFilters.metric_type" label="Metrica" :items="[{title:'Toate',value:''},...metricTypes]" item-title="title" item-value="value" density="compact" hide-details clearable />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-select v-model="logFilters.status" label="Status" :items="[{title:'Toate',value:''},{title:'Success',value:'success'},{title:'Failed',value:'failed'}]" item-title="title" item-value="value" density="compact" hide-details clearable />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field v-model.number="logFilters.sim_id" label="SIM ID" type="number" density="compact" hide-details clearable />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field v-model.number="logFilters.limit" label="Limita" type="number" density="compact" hide-details />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-btn icon="mdi-magnify" color="primary" variant="tonal" size="small" @click="loadLogs" :loading="loadingLogs" />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-data-table
              :headers="logHeaders"
              :items="logs"
              :loading="loadingLogs"
              density="compact"
              class="elevation-1"
              items-per-page="25"
            >
              <template #[`item.status`]="{ item }">
                <v-chip size="small" :color="item.status === 'success' ? 'success' : 'error'">{{ item.status }}</v-chip>
              </template>
              <template #[`item.executed_at`]="{ item }">
                {{ formatDate(item.executed_at) }}
              </template>
              <template #[`item.current_value`]="{ item }">
                {{ item.current_value }} / {{ item.threshold_value }} {{ metricUnitFor(item.metric_type) }}
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-window-item>
    </v-window>

    <!-- Dialogs -->
    <TrafficAlertRuleDialog
      v-model="showRuleDialog"
      :rule="editingRule"
      :metric-types="metricTypes"
      :network-types="networkTypes"
      :period-types="periodTypes"
      :action-types="actionTypes"
      :scope-types="scopeTypes"
      :profile-options="profiles"
      :show-profile-select="!ruleDialogForProfile"
      :default-profile-id="ruleDialogForProfile ? selectedProfileId : null"
      @saved="onRuleSaved"
    />

    <LimitationProfileDialog
      v-model="showProfileDialog"
      :profile="editingProfile"
      @saved="onProfileSaved"
    />

    <ProfileAssignmentDialog
      v-model="showAssignmentDialog"
      :profile-id="selectedProfileId"
      @saved="onAssignmentSaved"
    />

    <!-- Dry Run Results -->
    <v-dialog v-model="showDryRunResults" width="80vw">
      <v-card>
        <v-toolbar color="info" density="compact">
          <v-icon class="ml-4">mdi-play-circle-outline</v-icon>
          <v-toolbar-title>Rezultate Dry Run</v-toolbar-title>
          <v-chip class="mr-2" variant="flat" color="white" size="small">{{ dryRunResults.length }} alerte</v-chip>
          <v-btn icon="mdi-close" variant="text" @click="showDryRunResults = false" />
        </v-toolbar>
        <v-card-text class="pa-4">
          <div v-if="dryRunResults.length === 0" class="text-grey text-center pa-4">Nicio alerta nu ar fi declansata</div>
          <v-data-table v-else :headers="dryRunHeaders" :items="dryRunResults" density="compact" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="flat" color="info" @click="showDryRunResults = false">Inchide</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirm -->
    <v-dialog v-model="showDeleteConfirm" max-width="400">
      <v-card>
        <v-card-title>Confirmare Stergere</v-card-title>
        <v-card-text>{{ deleteMessage }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showDeleteConfirm = false">Anuleaza</v-btn>
          <v-btn color="error" variant="flat" @click="executeDelete" :loading="deleting">Sterge</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import NavBar from '@/components/NavBar.vue'
import TrafficAlertRuleDialog from '@/components/dialogs/TrafficAlertRuleDialog.vue'
import LimitationProfileDialog from '@/components/dialogs/LimitationProfileDialog.vue'
import ProfileAssignmentDialog from '@/components/dialogs/ProfileAssignmentDialog.vue'
import {
  getTrafficAlertRules, toggleTrafficAlertRule, deleteTrafficAlertRule,
  getLimitationProfiles, deleteLimitationProfile,
  getProfileAssignments, deleteProfileAssignment,
  getTrafficAlertLogs, trafficAlertsDryRun, processTrafficAlerts, getTrafficAlertEnums
} from '@/composables/useTrafficAlertsAPI'

export default {
  name: 'TrafficAlertRulesView',
  components: { NavBar, TrafficAlertRuleDialog, LimitationProfileDialog, ProfileAssignmentDialog },
  data() {
    return {
      activeTab: 'rules',
      // Enums
      metricTypes: [], networkTypes: [], periodTypes: [], actionTypes: [], scopeTypes: [],
      // Rules tab
      rules: [], loadingRules: false,
      ruleFilters: { metric_type: '', action_type: '', enabled: '', standaloneOnly: false },
      ruleHeaders: [
        { title: 'ID', key: 'id', width: '50px' },
        { title: 'Nume', key: 'name' },
        { title: 'Metrica', key: 'metric_type' },
        { title: 'Prag', key: 'threshold_value' },
        { title: 'Retea', key: 'network_type' },
        { title: 'Perioada', key: 'period_type' },
        { title: 'Actiune', key: 'action_type' },
        { title: 'Cooldown', key: 'cooldown_hours', width: '80px' },
        { title: 'Aplicare', key: 'scope_type_label' },
        { title: 'Tinta', key: 'target', sortable: false },
        { title: 'Profil', key: 'profile_name' },
        { title: 'Activa', key: 'enabled', width: '80px' },
        { title: '', key: 'actions', sortable: false, width: '100px' }
      ],
      // Profiles tab
      profiles: [], loadingProfiles: false,
      selectedProfileId: null,
      profileRules: [], loadingProfileRules: false,
      assignments: [], loadingAssignments: false,
      profileRuleHeaders: [
        { title: 'Nume', key: 'name' },
        { title: 'Metrica', key: 'metric_type' },
        { title: 'Prag', key: 'threshold_value' },
        { title: 'Actiune', key: 'action_type' },
        { title: 'Activa', key: 'enabled', width: '70px' },
        { title: '', key: 'actions', sortable: false, width: '90px' }
      ],
      // Logs tab
      logs: [], loadingLogs: false,
      logFilters: { metric_type: '', status: '', sim_id: null, limit: 100 },
      logHeaders: [
        { title: 'ID', key: 'id', width: '50px' },
        { title: 'Regula', key: 'rule_name' },
        { title: 'SIM', key: 'sim_msisdn' },
        { title: 'Client', key: 'client_name' },
        { title: 'Consum / Prag', key: 'current_value' },
        { title: 'Actiune', key: 'action_type' },
        { title: 'Status', key: 'status' },
        { title: 'Mesaj', key: 'message' },
        { title: 'Data', key: 'executed_at' }
      ],
      // Dialogs
      showRuleDialog: false, editingRule: null, ruleDialogForProfile: false,
      showProfileDialog: false, editingProfile: null,
      showAssignmentDialog: false,
      // Delete
      showDeleteConfirm: false, deleteMessage: '', deleteCallback: null, deleting: false,
      // Process & Dry run
      loadingProcess: false,
      loadingDryRun: false, showDryRunResults: false, dryRunResults: [],
      dryRunHeaders: [
        { title: 'Regula', key: 'rule_name' },
        { title: 'SIM MSISDN', key: 'sim_msisdn' },
        { title: 'Client', key: 'client_name' },
        { title: 'Metrica', key: 'metric_type' },
        { title: 'Consum', key: 'current_value' },
        { title: 'Prag', key: 'threshold_value' },
        { title: 'Actiune', key: 'action_type' },
        { title: 'Profil', key: 'profile_name' }
      ]
    }
  },
  computed: {
    selectedProfile() {
      return this.profiles.find(p => p.id === this.selectedProfileId)
    }
  },
  async mounted() {
    await this.loadEnums()
    this.loadRules()
    this.loadProfiles()
  },
  methods: {
    // ==================== LOAD DATA ====================
    async loadEnums() {
      const token = this.$store.state.token
      const result = await getTrafficAlertEnums(token)
      if (!result.error) {
        this.metricTypes = result.metric_types || []
        this.networkTypes = result.network_types || []
        this.periodTypes = result.period_types || []
        this.actionTypes = result.action_types || []
        this.scopeTypes = result.scope_types || []
      }
    },
    async loadRules() {
      this.loadingRules = true
      const token = this.$store.state.token
      const filters = {}
      if (this.ruleFilters.metric_type) filters.metric_type = this.ruleFilters.metric_type
      if (this.ruleFilters.action_type) filters.action_type = this.ruleFilters.action_type
      if (this.ruleFilters.enabled) filters.enabled = this.ruleFilters.enabled
      if (this.ruleFilters.standaloneOnly) filters.standalone = 'true'
      const result = await getTrafficAlertRules(token, filters)
      if (!result.error) this.rules = result.rules || []
      this.loadingRules = false
    },
    async loadProfiles() {
      this.loadingProfiles = true
      const token = this.$store.state.token
      const result = await getLimitationProfiles(token)
      if (!result.error) this.profiles = result.profiles || []
      this.loadingProfiles = false
    },
    async loadProfileRules() {
      if (!this.selectedProfileId) return
      this.loadingProfileRules = true
      const token = this.$store.state.token
      const result = await getTrafficAlertRules(token, { limitation_profile_id: this.selectedProfileId })
      if (!result.error) this.profileRules = result.rules || []
      this.loadingProfileRules = false
    },
    async loadAssignments() {
      if (!this.selectedProfileId) return
      this.loadingAssignments = true
      const token = this.$store.state.token
      const result = await getProfileAssignments(token, this.selectedProfileId)
      if (!result.error) this.assignments = result.assignments || []
      this.loadingAssignments = false
    },
    async loadLogs() {
      this.loadingLogs = true
      const token = this.$store.state.token
      const filters = {}
      if (this.logFilters.metric_type) filters.metric_type = this.logFilters.metric_type
      if (this.logFilters.status) filters.status = this.logFilters.status
      if (this.logFilters.sim_id) filters.sim_id = this.logFilters.sim_id
      filters.limit = this.logFilters.limit || 100
      const result = await getTrafficAlertLogs(token, filters)
      if (!result.error) this.logs = result.logs || []
      this.loadingLogs = false
    },

    // ==================== RULES ====================
    openRuleDialog(rule) {
      this.editingRule = rule
      this.ruleDialogForProfile = false
      this.showRuleDialog = true
    },
    openRuleDialogForProfile() {
      this.editingRule = { limitation_profile_id: this.selectedProfileId }
      this.ruleDialogForProfile = true
      this.showRuleDialog = true
    },
    onRuleSaved() {
      this.loadRules()
      if (this.selectedProfileId) this.loadProfileRules()
    },
    async toggleRule(rule) {
      const token = this.$store.state.token
      await toggleTrafficAlertRule(token, rule.id)
      this.loadRules()
      if (this.selectedProfileId) this.loadProfileRules()
    },
    confirmDeleteRule(rule) {
      this.deleteMessage = `Stergeti regula "${rule.name}"?`
      this.deleteCallback = async () => {
        const token = this.$store.state.token
        await deleteTrafficAlertRule(token, rule.id)
        this.loadRules()
        if (this.selectedProfileId) this.loadProfileRules()
      }
      this.showDeleteConfirm = true
    },

    // ==================== PROFILES ====================
    selectProfile(profile) {
      this.selectedProfileId = this.selectedProfileId === profile.id ? null : profile.id
      if (this.selectedProfileId) {
        this.loadProfileRules()
        this.loadAssignments()
      }
    },
    openProfileDialog(profile) {
      this.editingProfile = profile
      this.showProfileDialog = true
    },
    onProfileSaved() {
      this.loadProfiles()
    },
    confirmDeleteProfile(profile) {
      this.deleteMessage = `Stergeti profilul "${profile.name}" si toate regulile asociate?`
      this.deleteCallback = async () => {
        const token = this.$store.state.token
        await deleteLimitationProfile(token, profile.id)
        if (this.selectedProfileId === profile.id) this.selectedProfileId = null
        this.loadProfiles()
      }
      this.showDeleteConfirm = true
    },

    // ==================== ASSIGNMENTS ====================
    onAssignmentSaved() {
      this.loadAssignments()
      this.loadProfiles()
    },
    confirmDeleteAssignment(a) {
      const label = a.imsi_group_name ? `grupul "${a.imsi_group_name}"` : `SIM #${a.sim_id}`
      this.deleteMessage = `Stergeti asignarea pentru ${label}?`
      this.deleteCallback = async () => {
        const token = this.$store.state.token
        await deleteProfileAssignment(token, a.id)
        this.loadAssignments()
        this.loadProfiles()
      }
      this.showDeleteConfirm = true
    },

    // ==================== DELETE CONFIRM ====================
    async executeDelete() {
      this.deleting = true
      if (this.deleteCallback) await this.deleteCallback()
      this.deleting = false
      this.showDeleteConfirm = false
    },

    // ==================== PROCESS ALERTS ====================
    async runProcessAlerts() {
      this.loadingProcess = true
      const token = this.$store.state.token
      const result = await processTrafficAlerts(token, 'sync')
      this.loadingProcess = false
      if (!result.error) {
        alert(result.message)
        this.loadLogs()
      } else {
        alert(result.message || 'Eroare la procesare')
      }
    },

    // ==================== DRY RUN ====================
    async runDryRun() {
      this.loadingDryRun = true
      const token = this.$store.state.token
      const result = await trafficAlertsDryRun(token)
      this.loadingDryRun = false
      if (!result.error) {
        this.dryRunResults = result.results || []
        this.showDryRunResults = true
      }
    },

    // ==================== HELPERS ====================
    metricColor(type) {
      return { data_mb: 'blue', sms_count: 'green', voice_minutes: 'orange', total_cost: 'red' }[type] || 'grey'
    },
    actionColor(type) {
      return { notify_email: 'info', notify_in_app: 'primary', notify_both: 'purple', suspend_sim: 'error' }[type] || 'grey'
    },
    metricUnitFor(type) {
      return { data_mb: 'MB', sms_count: 'msg', voice_minutes: 'min', total_cost: 'EUR' }[type] || ''
    },
    formatDate(d) {
      if (!d) return '-'
      return new Date(d).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    }
  }
}
</script>
